/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */

import {JsonLdItem, LoadableStateInterface, LoadStatus, LoadStatusInterface} from '@/store/types';

/**
 * Trophy cabinet module state interface.
 * Represents a trophy cabinet module including the currently selected mode.
 */
export interface TrophyStateInterface extends LoadableStateInterface {
    speciesAchievements: SpeciesAchievementInterface[];
    challengeAchievements?: ChallengeAchievementInterface;
    mode: TrophyCabinetMode;
    timestamp: number;
}

/**
 * Trophy cabinet module state class.
 * Provides initial state.
 */
export class TrophyState implements TrophyStateInterface {
    public speciesAchievements: SpeciesAchievementInterface[] = [];
    public challengeAchievements?: ChallengeAchievementInterface;
    public mode: TrophyCabinetMode = TrophyCabinetMode.BORN;
    public timestamp: number = 0;
    public loadStatus: LoadStatusInterface = new LoadStatus();
}

/**
 * CC almanac interface to represent a player's cc achievements.
 */
export interface ChallengeAchievementInterface extends JsonLdItem {
    almanac: CommunityChallengeAlmanac[];
    achievement: AchievementInterface[];
    contribution: number;
}

/**
 * Interface representing request to get cc achievements.
 */
export interface CommunityChallengeAchievementsRequestInterface {
    active?: number;
    challenges: number[];
    milestones: number[];
}

/**
 * Representation of an acievement interface for community challenges.
 */
export interface AchievementInterface extends JsonLdItem {
    milestone: string;
    claimable: boolean;
    claimedAt: string;
    claimedAtTimestamp: number;
}

/**
 * CC almanac interface to represent a player's cc achievements.
 */
export interface CommunityChallengeAlmanac extends JsonLdItem {
    challenge: string;
    total: number;
    claimedAt?: string;
    claimedAtTimestamp: number;
    claimable: boolean;
}

/**
 * Species achievement interface representing a species trophy statistics for a franchise.
 */
export interface SpeciesAchievementInterface extends JsonLdItem {
    bought: number;
    sold: number;
    born: number;
    released: number;
    species: string;
    [key: string]: string|number;
}

/**
 * RewardAchievement represents the available rewards for a challenge and the user's relative success.
 */
export interface RewardAchievement {
    personal?: boolean;
    completion?: boolean;
}

/**
 * Represents and achievement set.
 */
export interface RewardAchievementSet {
    RewardAvatarGlasses?: RewardAchievement;
    RewardAvatarShorts?: RewardAchievement;
    RewardAvatarShirt?: RewardAchievement;
    RewardAvatarHat?: RewardAchievement;
    RewardCoin?: RewardAchievement;
    RewardAnimal?: RewardAchievement;
    RewardCredit?: RewardAchievement;
    [key: string]: RewardAchievement|undefined;
}

/**
 * Trophy Cabinet Mode
 */
export enum TrophyCabinetMode {
    CC = 'cc',
    BORN = 'born',
    RELEASED = 'released',
    BOUGHT = 'bought',
    SOLD = 'sold',
}

/**
 * Trophy Grades
 */
export enum TrophyGrade {
    GOLD = 'gold',
    SILVER = 'silver',
    BRONZE = 'bronze',
    NONE = 'none',
}

/**
 * Reward types
 */
export enum RewardType {
    RewardCredit = 'RewardCredit',
    RewardCoin = 'RewardCoin',
    RewardAvatarGlasses = 'RewardAvatarGlasses',
    RewardAvatarShorts = 'RewardAvatarShorts',
    RewardAvatarShirt = 'RewardAvatarShirt',
    RewardAvatarHat = 'RewardAvatarHat',
    RewardAnimal = 'RewardAnimal',
}
